import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaArrowRight } from "react-icons/fa";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import MyAccountTab from "./tabs/MyAccountTab";
import ConfigurationsTab from "./tabs/ConfigurationsTab";
import "./AccountSettingsModal.scss";

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"account" | "configurations">(
    "account",
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.is_admin === true;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="account-settings-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="account-settings-modal"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Gray top area: user info + upgrade banner + tabs */}
            <div className="modal-top">
              <div className="modal-header">
                <div className="modal-user-summary">
                  <p className="modal-user-email">{user?.email}</p>
                  <h2 className="modal-user-name">{user?.name}</h2>
                </div>
                <div className="modal-header-actions">
                  {activeTab === "account" && (
                    <button
                      type="submit"
                      form="my-account-form"
                      className="modal-save-btn"
                    >
                      Save Changes
                    </button>
                  )}
                  <button
                    className="modal-close-btn"
                    onClick={onClose}
                    aria-label="Close modal"
                    type="button"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              {/* Upgrade Plan banner */}
              {!isAdmin && (
                <div className="modal-upgrade-banner">
                  <div className="modal-upgrade-text">
                    <span className="modal-upgrade-icon">i</span>
                    <p>
                      You have used 43% of your subscription's total usage in
                      April. Upgrade to increase your limit.
                    </p>
                  </div>
                  <a href="/subscription" className="modal-upgrade-link">
                    Upgrade Plan <FaArrowRight />
                  </a>
                </div>
              )}

              {/* Tab Navigation */}
              <div className="modal-tabs">
                <button
                  className={`tab-btn ${activeTab === "account" ? "active" : ""}`}
                  onClick={() => setActiveTab("account")}
                >
                  My Account
                </button>
                {isAdmin && (
                  <button
                    className={`tab-btn ${activeTab === "configurations" ? "active" : ""}`}
                    onClick={() => setActiveTab("configurations")}
                  >
                    Configurations
                  </button>
                )}
              </div>
            </div>

            {/* White content area */}
            <div className="modal-content">
              <div className="modal-content-panel">
                <AnimatePresence mode="wait">
                  {activeTab === "account" && (
                    <motion.div
                      key="account"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <MyAccountTab />
                    </motion.div>
                  )}
                  {activeTab === "configurations" && (
                    <motion.div
                      key="configurations"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <ConfigurationsTab />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AccountSettingsModal;
